import articleImage from "../../assets/functional_c_plus_plus.jpg";

export interface Article {
  title: string;
  body: string;
  content?: string;
  image?: string;
  category?: string;
}

export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
};

export const articles: Article[] = [
  {
    title: "Functional Programming in C++?",
    category: "C++",
    body: "Philosophy, not features; in a language built for objects.",
    content: `
      Functional programming has gained significant traction in modern C++ development. 
      With lambdas, std::function, and ranges in C++20, we can now incorporate 
      functional concepts more naturally into our codebases.

      This article explores how to leverage these features effectively while avoiding common pitfalls.
      We'll cover what functional programming is, why it matters, and how to apply it in C++.

      Functional programming treats functions as <strong>first-class citizens</strong>; they can be passed as arguments, returned from other functions, and assigned to variables like any other value.

      <strong>Key benefits include:</strong>
      <ul>
        <li><strong>Testability:</strong> Pure functions with no side effects simplify unit testing.</li>
        <li><strong>Immutability:</strong> Avoiding mutable state reduces bugs and eases reasoning.</li>
        <li><strong>Modularity:</strong> Small, composable functions promote reuse.</li>
        <li><strong>Concurrency:</strong> Stateless functions enable safer parallelism and lazy evaluation.</li>
        <li><strong>Referential Transparency:</strong> Functions can be replaced with their results without changing behavior.</li>
        <li><strong>Flexible Composition:</strong> Currying, partial application, and memoization support advanced patterns.</li>
      </ul>
      While C++ isn't a purely functional language, we can adopt these techniques using:
      <ul>
        <li><strong>Lambdas</strong> for inline anonymous functions</li>
        <li><strong>std::function</strong> for type-erased callable storage</li>
        <li><strong>std::bind</strong> for argument binding and partial application</li>
        <li><strong>Ranges (C++20)</strong> for composable, lazy data transformations</li>
      </ul>
      Advanced patterns like memoization and algebraic data types require custom implementation 
      or third-party libraries, but the core functional toolkit in modern C++ is substantial.

      <strong>Memoization</strong> caches function results to avoid redundant computation. It only works correctly for pure functions, since their outputs are deterministic. However, the memoization mechanism itself is <strong>impure</strong>; it mutates a cache. This is a pragmatic trade-off: sacrificing implementation purity for performance while preserving identical input-output behavior.

      In short; C++ will never be Haskell, and that's fine. The goal isn't purity for its own sake; it's writing code that's easier to test, reason about, and maintain. By selectively applying functional techniques where they fit, you get practical benefits without fighting the language.

      <strong>Start small:</strong> favor const, prefer pure functions where possible, use lambdas to compose behavior, and reach for ranges when transforming data. The functional mindset is more valuable than any single feature.
    `,
    image: articleImage,
  },
];

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find((article) => generateSlug(article.title) === slug);
};
